import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FolderKanban,
  FileText,
  MessageSquare,
  Receipt,
  Clock,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Target,
  ClipboardList,
  Users,
  TrendingUp,
  Files,
  Calendar,
  Mail,
  Video,
  Play,
  MapPin,
} from 'lucide-react';
import useAuthStore from '../../context/authStore';
import useClientServices from '../../hooks/useClientServices';
import { projectsAPI, messagesAPI, invoicesAPI } from '../../services/api';
import { getStatusLabel, getStatusBadgeClass } from '../../utils/statusHelpers';
import { isClientWorkerRole } from '../../utils/roles';
import { BRAND } from '../../config/branding';
import FacilityRequestPolicyPrompt from '../../components/FacilityRequestPolicyPrompt';

export default function DashboardPage() {
  const { user, isInternalTeam, isAdmin, token } = useAuthStore();
  const { services, loading: servicesLoading } = useClientServices();

  // If this user is an internal team member, redirect them to the standalone admin portal for security.
  if (isInternalTeam() && token) {
    const adminUrl = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
    if (typeof window !== 'undefined') {
      window.location.assign(adminUrl);
    }
    return null;
  }

  // For training-only client workers, show a simplified training dashboard
  if (isClientWorkerRole(user)) {
    const { data: myLearning, isLoading: isLearningLoading } = useQuery({
      queryKey: ['my-learning'],
      queryFn: () => fetch('/api/lms/enrollments/my-learning', { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
      enabled: !!token,
    });

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-secondary-900">Your Training</h1>
          <p className="mt-1 text-secondary-600">Assigned training and progress.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary-900">Assigned Courses</h2>
              <Link to="/training" className="text-sm text-primary-600 hover:text-primary-700">View all</Link>
            </div>
            <div className="p-4">
              {isLearningLoading ? (
                <p className="text-secondary-500">Loading…</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-2xl font-bold">{(myLearning?.stats?.totalEnrolled || 0)}</p>
                  <p className="text-sm text-secondary-600">Courses assigned to you</p>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary-900">Progress</h2>
            </div>
            <div className="p-4">
              <p className="text-secondary-600">See your progress and certificates on the training page.</p>
              <Link to="/training" className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700">Go to Training</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const [upcomingEvents, setUpcomingEvents] = useState([]);

  // Fetch dashboard data
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { limit: 5 }],
    queryFn: () => projectsAPI.getAll({ limit: 5 }),
    select: (res) => res.data,
  });

  // Use server-side summary for active project counts
  const { data: projectsSummary, error: projectsSummaryError } = useQuery({
    queryKey: ['projects-summary'],
    queryFn: () => projectsAPI.getSummary(),
    // axios returns the full response; we only want the response body
    select: (res) => res.data,
  });

  // Active Projects = Ready + In Progress + In Review (from summary only — never fall back to total which includes completed)
  const readyCount = projectsSummary?.ready ?? 0;
  const inProgressCount = projectsSummary?.inProgress ?? 0;
  const inReviewCount = projectsSummary?.counts?.in_review ?? 0;
  const activeProjectsCount = projectsSummary ? (readyCount + inProgressCount + inReviewCount) : null;

  const { data: messagesData } = useQuery({
    queryKey: ['messages-unread'],
    queryFn: () => messagesAPI.getUnreadCount(),
    select: (res) => res.data,
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['invoices', { status: 'sent', limit: 5 }],
    queryFn: () => invoicesAPI.getAll({ status: 'sent', limit: 5 }),
    select: (res) => res.data,
    // Only fetch invoices for headquarters admins — facility users should not see invoices
    enabled: user?.role === 'org_admin',
  });

  const { data: pendingActionsData } = useQuery({
    queryKey: ['pending-actions'],
    queryFn: () => fetch('/api/documents/pending-actions', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.json()),
    enabled: !!token && services?.consulting?.enabled,
  });

  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => fetch('/api/notifications?limit=5', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.json()),
    enabled: !!token && services?.consulting?.enabled,
  });

  // Fetch calendar events
  useEffect(() => {
    if (token) {
      // Fetch upcoming calendar events
      const now = new Date();
      const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      fetch(`/api/calendar/events?start=${now.toISOString()}&end=${weekLater.toISOString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => setUpcomingEvents((data.events || []).slice(0, 4)))
        .catch(() => {});
    }
  }, [token]);

  const stats = [
    {
      name: 'Active Projects',
      // Overall active (combined) with breakdown for Ready and In Progress
      value: activeProjectsCount,
      breakdown: {
        ready: readyCount,
        inProgress: inProgressCount,
        inReview: inReviewCount,
      },
      icon: FolderKanban,
      href: '/projects',
      color: 'bg-blue-500',
      service: 'consulting',
    },

    {
      name: 'Completed Projects',
      // Show completed projects count from server summary
      value: projectsSummary ? (projectsSummary?.counts?.completed ?? 0) : null,
      icon: Files,
      href: '/projects?status=completed',
      color: 'bg-amber-500',
      service: 'consulting',
    },
    {
      name: 'Unread Messages',
      value: messagesData?.unreadCount || 0,
      icon: MessageSquare,
      href: '/messages',
      color: 'bg-green-500',
    },
  ];

  const pendingItems = useMemo(() => {
    const items = [];
    const notifications = notificationsData?.notifications || [];
    const actions = pendingActionsData?.actions || [];

    notifications.forEach((notification) => {
      items.push({
        id: notification._id,
        type: notification.type || 'notification',
        title: notification.title || 'Notification',
        description: notification.message || '',
        createdAt: notification.createdAt,
        documentName: notification.relatedEntity?.name || '',
      });
    });

    actions.forEach((action) => {
      items.push({
        id: action._id,
        type: action.type || 'action',
        title: action.title || 'Update',
        description: action.description || action.documentName || '',
        createdAt: action.createdAt,
        documentName: action.documentName || '',
      });
    });

    return items
      .filter((item) => item.createdAt)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);
  }, [notificationsData, pendingActionsData]);

  const getPendingStyle = (type) => {
    const key = type || '';
    if (key.includes('review') || key.includes('document_review_required')) {
      return { icon: Clock, bg: 'bg-amber-100', text: 'text-amber-600' };
    }
    if (key.includes('approved')) {
      return { icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-600' };
    }
    if (key.includes('message')) {
      return { icon: MessageSquare, bg: 'bg-blue-100', text: 'text-blue-600' };
    }
    if (key.includes('comment')) {
      return { icon: MessageSquare, bg: 'bg-purple-100', text: 'text-purple-600' };
    }
    return { icon: AlertCircle, bg: 'bg-secondary-100', text: 'text-secondary-600' };
  };

  // Only show the invoices stat to headquarters admins
  if (user?.role === 'org_admin') {
    stats.push({
      name: 'Outstanding Invoices',
      value: invoicesData?.total || 0,
      icon: Receipt,
      href: '/invoices',
      color: 'bg-purple-500',
    });
  }

  // Filter stats based on enabled services
  const visibleStats = stats.filter(stat => {
    if (stat.service === 'consulting') {
      return services?.consulting?.enabled;
    }
    return true; // Messages and Invoices don't have service restrictions
  });

  const formatEventTime = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let dayStr;
    if (date.toDateString() === today.toDateString()) {
      dayStr = 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dayStr = 'Tomorrow';
    } else {
      dayStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${dayStr} at ${timeStr}`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-secondary-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="mt-1 text-secondary-600">Here's what's happening with your projects.</p>
      </div>

      {user?.role === 'org_admin' && <FacilityRequestPolicyPrompt />}





      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {visibleStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="card p-6 hover:shadow-md transition-shadow group"
              title={stat.breakdown ? "Active = Ready + In Progress. Ready = projects set up and awaiting work; In Progress = work ongoing." : undefined}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between">
                    <p className="text-2xl font-bold text-secondary-900">{stat.value ?? '—'}</p>
                  </div>
                  <p className="text-sm text-secondary-600">{stat.name}</p>
                  {stat.breakdown && (
                    <div className="mt-2 text-xs text-secondary-500 space-y-2 opacity-0 max-h-0 overflow-hidden transition-all duration-200 group-hover:opacity-100 group-hover:max-h-40">
                      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
                        <span className="text-secondary-500">Ready</span>
                        <span className="font-medium text-secondary-700">{stat.breakdown.ready}</span>
                        <span className="text-secondary-500">In Progress</span>
                        <span className="font-medium text-secondary-700">{stat.breakdown.inProgress}</span>
                        <span className="text-secondary-500">In Review</span>
                        <span className="font-medium text-secondary-700">{stat.breakdown.inReview}</span>
                      </div>
                      {(() => {
                        const total = stat.breakdown.ready + stat.breakdown.inProgress + stat.breakdown.inReview;
                        const safeTotal = total > 0 ? total : 1;
                        const readyWidth = (stat.breakdown.ready / safeTotal) * 100;
                        const progressWidth = (stat.breakdown.inProgress / safeTotal) * 100;
                        const reviewWidth = (stat.breakdown.inReview / safeTotal) * 100;
                        return (
                          <div className="w-full h-1.5 rounded-full overflow-hidden bg-secondary-100 flex">
                            <div className="h-1.5 bg-primary-200" style={{ width: `${readyWidth}%` }} />
                            <div className="h-1.5 bg-primary-400" style={{ width: `${progressWidth}%` }} />
                            <div className="h-1.5 bg-primary-600" style={{ width: `${reviewWidth}%` }} />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>



      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upcoming Calendar Events */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
              <Calendar size={20} className="text-green-600" />
              Upcoming Meetings
            </h2>
            <Link to="/calendar" className="text-sm text-primary-600 hover:text-primary-700">
              View calendar
            </Link>
          </div>
          <div className="divide-y divide-secondary-200">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event) => (
                <div key={event._id} className="flex items-start gap-4 p-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {event.meetingUrl ? (
                      <Video className="w-5 h-5 text-green-600" />
                    ) : (
                      <MapPin className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-secondary-900 truncate">{event.title}</p>
                    <p className="text-sm text-secondary-500">{formatEventTime(event.startTime)}</p>
                    {event.attendees?.length > 0 && (
                      <p className="text-xs text-secondary-400 mt-1">
                        {event.attendees.length} attendee{event.attendees.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  {event.meetingUrl && (
                    <a
                      href={event.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Join
                    </a>
                  )}
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-secondary-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-secondary-300" />
                <p>No upcoming meetings</p>
                <Link to="/calendar" className="text-green-600 hover:underline text-sm mt-1 inline-block">
                  Schedule a meeting
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Recent Projects - Only show if Consulting service enabled */}
        {services?.consulting?.enabled && (
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary-900">Recent Projects</h2>
              <Link to="/projects" className="text-sm text-primary-600 hover:text-primary-700">
                View all
              </Link>
            </div>
            <div className="divide-y divide-secondary-200">
              {projectsData?.projects?.length > 0 ? (
                projectsData.projects.map((project) => (
                  <Link
                    key={project._id}
                    to={`/projects/${project._id}`}
                    className="flex items-center gap-4 p-4 hover:bg-secondary-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-secondary-900 truncate">
                        {project.name}
                      </p>
                      <p className="text-sm text-secondary-500 truncate">
                        {project.clientId?.name}
                      </p>
                    </div>
                    <span className={`badge ${getStatusBadgeClass(project.status)}`}>
                      {getStatusLabel(project.status)}
                    </span>
                  </Link>
                ))
              ) : (
                <div className="p-8 text-center text-secondary-500">
                  No projects yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>



      {/* Pending Actions - Only show if Consulting service enabled */}
      {services?.consulting?.enabled && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-secondary-900">Pending Actions</h2>
          </div>
          <div className="divide-y divide-secondary-200">
            {pendingItems.length > 0 ? (
              pendingItems.map((item) => {
                const style = getPendingStyle(item.type);
                const Icon = style.icon;
                return (
                  <div key={item.id} className="flex items-start gap-4 p-4">
                    <div className={`w-8 h-8 ${style.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${style.text}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-secondary-900">{item.title}</p>
                      <p className="text-sm text-secondary-500">{item.description}</p>
                      {item.documentName && (
                        <p className="text-xs text-secondary-400 mt-1">
                          <strong>{item.documentName}</strong>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-secondary-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-secondary-300" />
                <p>All caught up!</p>
                <p className="text-sm mt-1">No pending actions</p>
              </div>
            )}
          </div>
        </div>
      )}


    </div>
  );
}
